import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Divider,
  IconButton,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormErrorMessage,
  SimpleGrid
} from '@chakra-ui/react';
import { ChevronRightIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';

const ComponentForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    category: '',
    description: '',
    icon: '',
    fields: [],
    template_path: '',
    preview_image: '',
    default_data: {},
    is_active: true,
    is_system: false,
    version: '1.0'
  });

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (tenantId) {
      fetchCategories();
      if (isEdit) {
        fetchComponent();
      }
    }
  }, [tenantId, id]);

  const fetchCategories = async () => {
    try {
      const response = await apiClient(`/component-categories/${tenantId}`);
      let categoriesData = [];
      if (Array.isArray(response)) {
        categoriesData = response.flat();
      } else if (response.data) {
        categoriesData = Array.isArray(response.data) ? response.data.flat() : [];
      }
      categoriesData = [...new Set(categoriesData)];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchComponent = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/page-components/${tenantId}/${id}`);
      
      if (response.error) {
        throw new Error(response.error);
      }

      const componentData = response.data || response;
      
      setFormData({
        name: componentData.name || '',
        identifier: componentData.identifier || '',
        category: componentData.category || '',
        description: componentData.description || '',
        icon: componentData.icon || '',
        fields: componentData.fields ? JSON.parse(componentData.fields) : [],
        template_path: componentData.template_path || '',
        preview_image: componentData.preview_image || '',
        default_data: componentData.default_data ? JSON.parse(componentData.default_data) : {},
        is_active: componentData.is_active !== undefined ? componentData.is_active : true,
        is_system: componentData.is_system !== undefined ? componentData.is_system : false,
        version: componentData.version || '1.0'
      });
    } catch (error) {
      console.error('Error fetching component:', error);
      toast({
        title: 'Error loading component',
        description: error.message || 'Failed to load component',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate identifier from name
    if (field === 'name' && !isEdit) {
      const identifier = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        identifier
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addField = () => {
    const newField = {
      name: '',
      type: 'text',
      label: '',
      required: false,
      default: '',
      options: [],
      min: null,
      max: null
    };

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const addFieldOption = (fieldIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { ...field, options: [...(field.options || []), ''] }
          : field
      )
    }));
  };

  const updateFieldOption = (fieldIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { 
              ...field, 
              options: field.options.map((opt, j) => j === optionIndex ? value : opt)
            }
          : field
      )
    }));
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { 
              ...field, 
              options: field.options.filter((_, j) => j !== optionIndex)
            }
          : field
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Component name is required';
    }

    if (!formData.identifier.trim()) {
      newErrors.identifier = 'Component identifier is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Validate fields
    formData.fields.forEach((field, index) => {
      if (!field.name.trim()) {
        newErrors[`field_${index}_name`] = 'Field name is required';
      }
      if (!field.label.trim()) {
        newErrors[`field_${index}_label`] = 'Field label is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors below',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        fields: JSON.stringify(formData.fields),
        default_data: JSON.stringify(formData.default_data)
      };

      const url = isEdit 
        ? `/page-components/${tenantId}/${id}`
        : `/page-components/${tenantId}`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await apiClient(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: `Component ${isEdit ? 'updated' : 'created'} successfully`,
        description: `"${formData.name}" has been ${isEdit ? 'updated' : 'created'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/component-library');
    } catch (error) {
      console.error('Error saving component:', error);
      toast({
        title: `Error ${isEdit ? 'updating' : 'creating'} component`,
        description: error.message || `Failed to ${isEdit ? 'update' : 'create'} component`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'richtext', label: 'Rich Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'url', label: 'URL' },
    { value: 'color', label: 'Color' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'select', label: 'Select' },
    { value: 'media', label: 'Media' },
    { value: 'media_multiple', label: 'Multiple Media' }
  ];

  if (!tenantId) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/component-library">
            Component Library
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>
            {isEdit ? 'Edit Component' : 'Create Component'}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">
              {isEdit ? 'Edit Component' : 'Create Component'}
            </Heading>
            <HStack spacing={3}>
              <Button
                leftIcon={<FiArrowLeft />}
                variant="outline"
                onClick={() => navigate('/component-library')}
              >
                Back
              </Button>
              <Button
                leftIcon={<FiSave />}
                colorScheme="blue"
                type="submit"
                isLoading={loading}
                loadingText={isEdit ? 'Updating...' : 'Creating...'}
              >
                {isEdit ? 'Update Component' : 'Create Component'}
              </Button>
            </HStack>
          </HStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {/* Basic Information */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md">Basic Information</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired isInvalid={errors.name}>
                    <FormLabel>Component Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., Hero Section"
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.identifier}>
                    <FormLabel>Identifier</FormLabel>
                    <Input
                      value={formData.identifier}
                      onChange={(e) => handleInputChange('identifier', e.target.value)}
                      placeholder="e.g., hero-section"
                      isReadOnly={isEdit}
                    />
                    <FormErrorMessage>{errors.identifier}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.category}>
                    <FormLabel>Category</FormLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      placeholder="Select category"
                    >
                      {categories.map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.category}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe what this component does..."
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Icon</FormLabel>
                    <Input
                      value={formData.icon}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      placeholder="e.g., FiImage"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Version</FormLabel>
                    <Input
                      value={formData.version}
                      onChange={(e) => handleInputChange('version', e.target.value)}
                      placeholder="e.g., 1.0"
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0}>Active</FormLabel>
                    <Switch
                      isChecked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0}>System</FormLabel>
                    <Switch
                      isChecked={formData.is_system}
                      onChange={(e) => handleInputChange('is_system', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Settings */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md">Settings</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Template Path</FormLabel>
                    <Input
                      value={formData.template_path}
                      onChange={(e) => handleInputChange('template_path', e.target.value)}
                      placeholder="e.g., components/hero-section.html"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Preview Image URL</FormLabel>
                    <Input
                      value={formData.preview_image}
                      onChange={(e) => handleInputChange('preview_image', e.target.value)}
                      placeholder="https://example.com/preview.jpg"
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Component Fields */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Component Fields</Heading>
                <Button
                  leftIcon={<FiPlus />}
                  size="sm"
                  onClick={addField}
                >
                  Add Field
                </Button>
              </HStack>
            </CardHeader>
            <CardBody>
              {formData.fields.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500" mb={4}>
                    No fields defined yet
                  </Text>
                  <Button
                    leftIcon={<FiPlus />}
                    onClick={addField}
                  >
                    Add Your First Field
                  </Button>
                </Box>
              ) : (
                <Accordion allowMultiple>
                  {formData.fields.map((field, index) => (
                    <AccordionItem key={index}>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <HStack>
                            <Text fontWeight="medium">
                              {field.label || field.name || `Field ${index + 1}`}
                            </Text>
                            <Badge>{field.type}</Badge>
                            {field.required && (
                              <Badge colorScheme="red" size="sm">Required</Badge>
                            )}
                          </HStack>
                        </Box>
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(index);
                          }}
                        />
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel>
                        <ComponentFieldForm
                          field={field}
                          index={index}
                          fieldTypes={fieldTypes}
                          updateField={updateField}
                          addFieldOption={addFieldOption}
                          updateFieldOption={updateFieldOption}
                          removeFieldOption={removeFieldOption}
                          errors={errors}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

const ComponentFieldForm = ({
  field,
  index,
  fieldTypes,
  updateField,
  addFieldOption,
  updateFieldOption,
  removeFieldOption,
  errors
}) => {
  return (
    <VStack spacing={4} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired isInvalid={errors[`field_${index}_name`]}>
          <FormLabel>Field Name</FormLabel>
          <Input
            value={field.name}
            onChange={(e) => updateField(index, 'name', e.target.value)}
            placeholder="e.g., title"
          />
          <FormErrorMessage>{errors[`field_${index}_name`]}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={errors[`field_${index}_label`]}>
          <FormLabel>Field Label</FormLabel>
          <Input
            value={field.label}
            onChange={(e) => updateField(index, 'label', e.target.value)}
            placeholder="e.g., Title"
          />
          <FormErrorMessage>{errors[`field_${index}_label`]}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>Field Type</FormLabel>
          <Select
            value={field.type}
            onChange={(e) => updateField(index, 'type', e.target.value)}
          >
            {fieldTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Default Value</FormLabel>
          <Input
            value={field.default || ''}
            onChange={(e) => updateField(index, 'default', e.target.value)}
            placeholder="Default value"
          />
        </FormControl>
      </SimpleGrid>

      <HStack>
        <FormControl display="flex" alignItems="center">
          <FormLabel mb={0}>Required</FormLabel>
          <Switch
            isChecked={field.required}
            onChange={(e) => updateField(index, 'required', e.target.checked)}
          />
        </FormControl>

        {(field.type === 'number') && (
          <>
            <FormControl>
              <FormLabel>Min Value</FormLabel>
              <Input
                type="number"
                value={field.min || ''}
                onChange={(e) => updateField(index, 'min', e.target.value)}
                placeholder="Min"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Max Value</FormLabel>
              <Input
                type="number"
                value={field.max || ''}
                onChange={(e) => updateField(index, 'max', e.target.value)}
                placeholder="Max"
              />
            </FormControl>
          </>
        )}
      </HStack>

      {field.type === 'select' && (
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontWeight="medium">Options</Text>
            <Button
              size="sm"
              leftIcon={<AddIcon />}
              onClick={() => addFieldOption(index)}
            >
              Add Option
            </Button>
          </HStack>
          <VStack spacing={2} align="stretch">
            {(field.options || []).map((option, optionIndex) => (
              <HStack key={optionIndex}>
                <Input
                  value={option}
                  onChange={(e) => updateFieldOption(index, optionIndex, e.target.value)}
                  placeholder={`Option ${optionIndex + 1}`}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  onClick={() => removeFieldOption(index, optionIndex)}
                />
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default ComponentForm;